
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Users, DollarSign, TrendingUp, UserCheck } from 'lucide-react';

export function MetricsCards() {
  const { data: metrics, isLoading } = useQuery({
    queryKey: ['admin-metrics'],
    queryFn: async () => {
      // Get total subscribers
      const { count: totalSubscribers } = await supabase
        .from('subscribers')
        .select('*', { count: 'exact', head: true });

      // Get active subscribers
      const { count: activeSubscribers } = await supabase
        .from('subscribers')
        .select('*', { count: 'exact', head: true })
        .eq('subscribed', true);

      // Get trial users
      const { count: trialUsers } = await supabase
        .from('subscribers')
        .select('*', { count: 'exact', head: true })
        .eq('is_trial', true);

      // Calculate MRR (assuming $9.95 for premium users)
      const { data: premiumUsers } = await supabase
        .from('subscribers')
        .select('subscription_tier')
        .eq('subscribed', true);

      const mrr = premiumUsers?.reduce((total, user) => {
        if (user.subscription_tier === 'premium') return total + 9.95;
        return total;
      }, 0) || 0;

      // Calculate conversion rate
      const conversionRate = totalSubscribers && activeSubscribers 
        ? ((activeSubscribers / totalSubscribers) * 100).toFixed(1)
        : '0';

      return {
        totalSubscribers: totalSubscribers || 0,
        activeSubscribers: activeSubscribers || 0,
        trialUsers: trialUsers || 0,
        mrr: mrr.toFixed(2),
        conversionRate
      };
    }
  });

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[...Array(4)].map((_, i) => (
          <Card key={i}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Loading...</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-8 bg-gray-200 animate-pulse rounded" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  const cards = [
    {
      title: "Total Subscribers",
      value: metrics?.totalSubscribers.toLocaleString() || '0',
      icon: Users,
      description: "All registered users"
    },
    {
      title: "Active Subscribers",
      value: metrics?.activeSubscribers.toLocaleString() || '0',
      icon: UserCheck,
      description: "Paying customers"
    },
    {
      title: "Monthly Revenue",
      value: `$${metrics?.mrr || '0'}`,
      icon: DollarSign,
      description: "MRR from subscriptions"
    },
    {
      title: "Conversion Rate",
      value: `${metrics?.conversionRate || '0'}%`,
      icon: TrendingUp,
      description: "Free to paid conversion"
    }
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      {cards.map((card, index) => (
        <Card key={index}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{card.title}</CardTitle>
            <card.icon className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{card.value}</div>
            <p className="text-xs text-muted-foreground">{card.description}</p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
